import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';

// Use empty string to leverage proxy in package.json (http://backend:4000)
const API_URL = process.env.REACT_APP_API_URL || '';

const registrationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
});

interface InvitationData {
  email: string;
  role: string;
  invitedBy: string;
  expiresAt: string;
}

interface RegistrationFormValues {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

const InvitationRegister: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('invite');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validate invitation token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No invitation token provided. Please use the link from your invitation email.');
        setLoading(false);
        return;
      }

      try {
        // Validate the invitation token (public endpoint)
        const response = await axios.get(`${API_URL}/api/admin/invitations/validate/${token}`);
        setInvitation(response.data.invitation);
        setError(null);
      } catch (err: any) {
        console.error('Token validation error:', err);
        if (err.response?.status === 404) {
          setError('Invalid or expired invitation. Please request a new invitation.');
        } else if (err.response?.status === 410) {
          setError('This invitation has already been used.');
        } else {
          setError(err.response?.data?.message || 'Failed to validate invitation token.');
        }
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (values: RegistrationFormValues, { setSubmitting }: any) => {
    if (!token || !invitation) return;

    try {
      // Register via the auth register endpoint with the invite token as query param
      await axios.post(`${API_URL}/api/auth/register?invite=${encodeURIComponent(token)}`, {
        email: invitation.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
      });

      setSuccess(true);
      setError(null);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: 'Account created successfully! Please sign in with your credentials.',
            email: invitation?.email,
          },
        });
      }, 2000);
    } catch (err: any) {
      console.error('Account setup error:', err);
      setError(err.response?.data?.message || 'Failed to set up account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues: RegistrationFormValues = {
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Paper elevation={10} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Validating your invitation...
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (error && !invitation) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={10} sx={{ p: 4, borderRadius: 2 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <BusinessIcon sx={{ fontSize: 60, color: 'error.main', mb: 1 }} />
              <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                Invalid Invitation
              </Typography>
            </Box>

            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                If you believe this is an error, please contact your administrator for a new
                invitation.
              </Typography>
              <Button
                component={RouterLink}
                to="/login"
                variant="outlined"
                size="large"
                sx={{ mt: 2 }}
              >
                Go to Login
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          py: 4,
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={10} sx={{ p: 4, borderRadius: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                Account Created!
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Your account has been successfully set up. You will be redirected to the login page
                shortly.
              </Typography>
              <CircularProgress size={40} sx={{ mt: 2 }} />
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={10} sx={{ p: 4, borderRadius: 2 }}>
          {/* Logo/Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <BusinessIcon sx={{ fontSize: 60, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              AJ Group Holdings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Governance Platform
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="h5" component="h2" gutterBottom textAlign="center">
            Complete Your Account Setup
          </Typography>

          {/* Invitation Info */}
          {invitation && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Email:</strong> {invitation.email}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Invited by:</strong> {invitation.invitedBy}
              </Typography>
              <Typography variant="body2">
                <strong>Role:</strong> {invitation.role}
              </Typography>
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Registration Form */}
          <Formik
            initialValues={initialValues}
            validationSchema={registrationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form>
                <Field name="firstName">
                  {({ field }: any) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="First Name"
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                      autoComplete="given-name"
                      autoFocus
                      sx={{ mb: 2 }}
                    />
                  )}
                </Field>

                <Field name="lastName">
                  {({ field }: any) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Last Name"
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                      autoComplete="family-name"
                      sx={{ mb: 2 }}
                    />
                  )}
                </Field>

                <Field name="password">
                  {({ field }: any) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Password *"
                      type={showPassword ? 'text' : 'password'}
                      required
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      autoComplete="new-password"
                      sx={{ mb: 2 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              aria-label="toggle password visibility"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Field>

                <Field name="confirmPassword">
                  {({ field }: any) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Confirm Password *"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                      helperText={touched.confirmPassword && errors.confirmPassword}
                      autoComplete="new-password"
                      sx={{ mb: 2 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                              aria-label="toggle confirm password visibility"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Field>

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
                  Password must be at least 8 characters and contain uppercase, lowercase, and
                  numbers.
                </Typography>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{ py: 1.5 }}
                >
                  {isSubmitting ? 'Setting up account...' : 'Complete Setup'}
                </Button>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Already have an account?{' '}
                    <Link component={RouterLink} to="/login" underline="hover">
                      Sign In
                    </Link>
                  </Typography>
                </Box>
              </Form>
            )}
          </Formik>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
            &copy; {new Date().getFullYear()} AJ Group Holdings. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default InvitationRegister;
